import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { Card, CardHeader, CardTitle, CardContent } from "~/components/ui/card";
import { BsStars } from "react-icons/bs";
import { getPokemonImageSrc, getPokemonImageAlt } from "~/lib/pokemon";

interface ShinyCardProps {
  imageUrl: string;
  shinyImageUrl: string | null;
  pokemonName: string;
  formName?: string;
  costumeName?: string;
  isShinyAvailable?: boolean;
  isUnreleased?: boolean;
}

const ShinyCard = ({
  imageUrl,
  shinyImageUrl,
  pokemonName,
  formName,
  costumeName,
  isShinyAvailable,
  isUnreleased,
}: ShinyCardProps) => {
  const locale = useLocale();
  const t = useTranslations("Pokemon");
  const fullName = [pokemonName, formName, costumeName]
    .filter(Boolean)
    .join(" ");
  const isMissingImages =
    imageUrl.includes("pokemon_0.icon") &&
    (!shinyImageUrl || shinyImageUrl.includes("pokemon_0.icon"));

  return (
    <Card id="shiny-comparison">
      <CardHeader className="pb-2">
        <CardTitle className="text-pory-blue-900 text-xl font-bold">
          {t("shinyComparison")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 py-8">
        {isMissingImages ? (
          <div className="flex items-center justify-center py-4 text-center">
            <p className="text-lg font-medium text-neutral-500">
              {t("unreleasedInGame", { name: fullName })}
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-around">
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <Image
                    src={getPokemonImageSrc(
                      imageUrl,
                      false,
                      shinyImageUrl ?? null,
                    )}
                    alt={getPokemonImageAlt(
                      pokemonName,
                      imageUrl,
                      false,
                      formName,
                      costumeName,
                      locale,
                    )}
                    width={160}
                    height={160}
                    priority
                    className="drop-shadow-xl transition-transform hover:scale-110"
                  />
                </div>
                <span className="text-pory-blue-900 bg-pory-blue-50 rounded-full px-3 py-1 text-sm font-bold">
                  {t("normal")}
                </span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <div className="absolute -top-4 -right-4 z-10 animate-pulse">
                    <BsStars className="text-2xl text-yellow-400" />
                  </div>
                  <Image
                    src={getPokemonImageSrc(
                      imageUrl,
                      true,
                      shinyImageUrl ?? null,
                    )}
                    alt={getPokemonImageAlt(
                      pokemonName,
                      imageUrl,
                      true,
                      formName,
                      costumeName,
                      locale,
                    )}
                    width={160}
                    height={160}
                    className="drop-shadow-xl transition-transform hover:scale-110"
                  />
                </div>
                <span className="flex items-center gap-1 rounded-full bg-yellow-50 px-3 py-1 text-sm font-bold text-yellow-700">
                  <BsStars className="text-xs text-yellow-500" />
                  {t("shiny")}
                </span>
              </div>
            </div>

            {isShinyAvailable !== undefined && (
              <div className="text-center font-medium">
                {isUnreleased ? (
                  <p className="text-neutral-500">
                    {t("unreleasedNotice", { name: fullName })}
                  </p>
                ) : isShinyAvailable ? (
                  <p className="flex items-center justify-center gap-2 text-green-700">
                    <BsStars className="text-yellow-500" />
                    {t("canBeShinyNotice", { name: fullName })}
                  </p>
                ) : (
                  <p className="text-neutral-500">{t("notVerifiedShiny")}</p>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ShinyCard;
